<script setup lang="ts">
import type { StoryblokRichTextNode } from '@storyblok/vue'
import type { StoryblokAsset } from '../../types'

interface Props {
  blok: {
    title?: StoryblokRichTextNode
    subtitle?: StoryblokRichTextNode
    actions: any[]
    hero?: StoryblokAsset
    hero_mobile?: StoryblokAsset
    body: any[]
    hero_background_image?: StoryblokAsset
    hero_background_image_mobile?: StoryblokAsset
    hero_background_color?: string
  }
}

const props = defineProps<Props>()
const imageOptimized = computed(() => props.blok.hero ? optimizeImage(props.blok.hero.filename) : undefined)
const imageOptimizedMobile = computed(() => props.blok.hero_mobile ? optimizeImage(props.blok.hero_mobile.filename) : undefined)
const heroBackgroundImageOptimized = computed(() => props.blok.hero_background_image ? optimizeImage(props.blok.hero_background_image.filename) : undefined)
const heroBackgroundImageOptimizedMobile = computed(() => props.blok.hero_background_image_mobile ? optimizeImage(props.blok.hero_background_image_mobile.filename) : undefined)
</script>

<template>
  <div
    :class="$style.container" from-gray-lighter to-white
    class="lg:rounded-t-64px"
  >
    <div
      v-editable="blok"
      class="pt-[60px] lg:pt-[140px] px-[10px] lg:px-[20px] gap-[60px] lg:rounded-t-64px"
    >
      <div col class="gap-[28px] lg:gap-[40px]">
        <div text-center class="max-w-[1280px] mx-auto">
          <StoryblokRichText v-if="blok.title" :doc="blok.title" />
        </div>

        <div text-center class="max-w-[640px] mx-auto  hidden md:block">
          <StoryblokRichText v-if="blok.subtitle" :doc="blok.subtitle" />
        </div>

        <div center gap-3>
          <StoryblokComponent v-for="action in blok.actions" :key="action._uid" :blok="action" />
        </div>
      </div>
    </div>
    <div
      v-if="blok.hero"
      v-editable="blok"
      :class="$style.hero"
      :style="{
        '--hero-background-color': blok.hero_background_color,
        '--hero-background-image': blok.hero_background_image ? `url(${heroBackgroundImageOptimized})` : 'none',
        '--hero-background-image-mobile': blok.hero_background_image_mobile ? `url(${heroBackgroundImageOptimizedMobile})` : 'none',
      }"
    >
      <img v-if="blok?.hero" :src="imageOptimized" :alt="blok.hero.alt" class="w-full max-w-[1280px] mx-a hidden md:block">
      <img v-if="blok?.hero_mobile" :src="imageOptimizedMobile" :alt="blok.hero_mobile.alt" class="w-full max-w-[640px] mx-a block md:hidden">
    </div>
    <div
      v-editable="blok"
    >
      <div col center class="pt-[60px] md:py-[60px] gap-[60px] px-[20px]">
        <StoryblokComponent v-for="section in blok.body" :key="section._uid" :blok="section" />
      </div>
    </div>
  </div>
</template>

<style module>
.container {
  background-image: linear-gradient(180deg, #DEF5FC 30%, rgba(176, 241, 255, 0.00) 100%);
}

.hero {
  background-color: var(--hero-background-color);
  background-image: var(--hero-background-image-mobile);
  background-size: cover;
  background-position: center;

  @screen md {
    background-image: var(--hero-background-image);
  }
}
</style>

<!--
hero color
bg image
hero image desktop
hero image mobile
 -->
